const API_URL: string | undefined = import.meta.env.VITE_API_URL as string | undefined;

import axios, { AxiosError, AxiosInstance, AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import { addHours } from 'date-fns';
import i18n from 'i18next';

import {
  ERROR_CODES,
  ERROR_CUSTOM_SNACKS,
  HTTP_STATUS_MESSAGES,
  getAxiosResponseErrorCodeExceptionsList,
  getAxiosResponseErrorMessageExceptionList,
  getAxiosResponseErrorUrlExceptionList,
} from '@constants';
import { accessToken } from '@core/storage';
import { Headers, StatusType } from '@enums';
import { addSnack, logOut, store } from '@store';
import { ApiErrorData, ApiRequestConfig } from '@types';
import { cleanupRequestTracker, tokenRefreshManager, tracker, validateValueEqual } from '@utils';

/**
 * Configured Axios instance for API requests
 *
 * Pre-configured with base URL, JSON response type, and custom interceptors
 * for authentication, request deduplication, and error handling.
 */
export const API: AxiosInstance = axios.create({
  baseURL: API_URL,
  responseType: 'json',
});

/**
 * Creates a unique key for request deduplication
 *
 * Generates a unique identifier based on HTTP method, URL, parameters, and body data.
 * Used to prevent duplicate API requests and implement request caching.
 *
 * @param config - Axios request configuration
 * @returns Unique string identifier for the request
 *
 * @example
 * ```typescript
 * const config = { method: 'GET', url: '/users', params: { page: 1 } };
 * const key = createRequestKey(config); // "GET-/users?{\"page\":1}"
 * ```
 */
const createRequestKey = (config: ApiRequestConfig): string => {
  const { url, method, params, data } = config;
  return `${method}-${url}${params ? `?${JSON.stringify(params)}` : ''}${data ? `-${JSON.stringify(data)}` : ''}`;
};

/**
 * Sets HTTP headers on an Axios instance
 *
 * Merges new headers with existing common headers on the specified Axios instance.
 * Preserves existing headers while adding or updating the provided ones.
 *
 * @param headers - Object containing header key-value pairs to set
 * @param axiosInstance - Axios instance to modify (defaults to main API instance)
 *
 * @example
 * ```typescript
 * setHeaders({
 *   'Content-Type': 'application/json',
 *   'X-Custom-Header': 'value'
 * });
 * ```
 */
export const setHeaders = (headers: { [key: string]: string }, axiosInstance: AxiosInstance = API) => {
  const commonHeaders = { ...axiosInstance.defaults.headers.common };
  Object.keys(headers).forEach((key) => (commonHeaders[key] = headers[key]));
  axiosInstance.defaults.headers.common = commonHeaders;
};

export function setInitialHeaders() {
  const access_token = accessToken.getItem();
  const headers: Record<string, string> = {};
  if (access_token) {
    API.defaults.headers.common[Headers.Authorization] = `Bearer ${access_token}`;
  }
  return headers;
}

setInitialHeaders();

API.interceptors.request.use(
  async (config: InternalAxiosRequestConfig): Promise<InternalAxiosRequestConfig> => {
    const apiConfig = config as ApiRequestConfig;
    const requestKey = createRequestKey(apiConfig);

    // Handle duplicate requests
    if (tracker.pendingRequests.has(requestKey)) {
      const controller = tracker.pendingRequests.get(requestKey);
      return new Promise((resolve, reject) => {
        controller?.signal.addEventListener('complete', (response) => {
          resolve(response as InternalAxiosRequestConfig);
        });
        controller?.signal.addEventListener('error', (error) => {
          reject(error as Error);
        });
      });
    }

    // Check if token needs refresh before making request
    const authHeader = config.headers?.[Headers.Authorization] as string | undefined;
    if (authHeader?.startsWith('Bearer ') && config.url !== '/user/refresh') {
      try {
        const refreshed = await tokenRefreshManager.refreshTokenIfNeeded();
        if (refreshed) {
          // Get the new token and update headers
          const newToken = accessToken.getItem();
          if (newToken) {
            config.headers[Headers.Authorization] = `Bearer ${newToken}`;
          }
        }
      } catch (error) {
        console.error('Token refresh failed in request interceptor:', error);
      }
    }

    // Set up request tracking
    const controller = new AbortController();
    tracker.pendingRequests.set(requestKey, controller);
    config.signal = controller.signal;
    apiConfig._requestKey = requestKey;

    return config;
  },
  (error) => Promise.reject(error as Error),
);

API.interceptors.response.use(
  (response: AxiosResponse) => {
    const config = response.config as ApiRequestConfig;
    cleanupRequestTracker(config._requestKey);
    return response;
  },
  async (error: AxiosError<ApiErrorData>) => {
    const config = error.config as ApiRequestConfig;

    let skipMessageDisplay = false;

    cleanupRequestTracker(config?._requestKey);

    if (!error.response && (config?._retryCount ?? 0) < tracker.MAX_RETRIES) {
      config._retryCount = (config._retryCount ?? 0) + 1;
      await new Promise((resolve) => setTimeout(resolve, tracker.RETRY_DELAYS[(config._retryCount ?? 1) - 1]));
      return API(config);
    }

    if (!error.response) {
      await Promise.all([
        store.dispatch({
          type: addSnack.type,
          payload: {
            type: StatusType.Error,
            message: i18n.t(`errors:SYSTEM_ERROR`),
          },
        }),
      ]);

      return Promise.reject(error);
    }

    const { status, data } = error.response;
    const errorCode = data.status_code ?? status ?? data?.code;
    const errorMessage = data.message ?? HTTP_STATUS_MESSAGES[status] ?? 'errors:SYSTEM_ERROR';
    const url = config?.url ?? '';

    // Handle 401 Unauthorized errors
    if (status === 401 && !config._isRetryAfterRefresh && url !== '/user/refresh') {
      try {
        // Attempt to refresh the token
        const refreshSuccess = await tokenRefreshManager.refreshTokenIfNeeded();

        if (refreshSuccess) {
          // Retry the original request with new token
          const newToken = accessToken.getItem();
          if (newToken && config.headers) {
            config.headers[Headers.Authorization] = `Bearer ${newToken}`;
            config._isRetryAfterRefresh = true;
            return API(config);
          }
        }
      } catch (refreshError) {
        console.error('Token refresh failed on 401:', refreshError);
      }

      // If refresh failed or we still can't proceed, logout
      tokenRefreshManager.handleLogout();
      store.dispatch(logOut());
      skipMessageDisplay = true;
      return Promise.reject(error);
    }

    const [codeExceptions, urlExceptions, messageExceptions] = await Promise.all([
      getAxiosResponseErrorCodeExceptionsList(),
      getAxiosResponseErrorUrlExceptionList(),
      getAxiosResponseErrorMessageExceptionList(),
    ]);

    if (validateValueEqual(status, 503)) {
      localStorage.setItem('maintenanceEndTime', addHours(new Date(), 24).toISOString());
      skipMessageDisplay = true;
    }

    skipMessageDisplay =
      skipMessageDisplay ||
      Boolean(url && urlExceptions.some((urlEx) => url.includes(urlEx))) ||
      Boolean(errorCode && codeExceptions.some((exception) => exception === String(errorCode))) ||
      Boolean(errorMessage && messageExceptions.some((exception) => exception === String(errorMessage)));

    if (!skipMessageDisplay && errorCode) {
      const toastMessage = i18n.exists(`errors:${errorCode}`)
        ? i18n.t(`errors:${errorCode}`, { message: errorMessage })
        : errorMessage;

      store.dispatch({
        type: addSnack.type,
        payload: ERROR_CUSTOM_SNACKS[errorCode.toString() as ERROR_CODES] || {
          type: StatusType.Error,
          message: toastMessage,
        },
      });
    }

    return Promise.reject(error);
  },
);

window.addEventListener('beforeunload', () => {
  tracker.pendingRequests.clear();
});
